/* eslint-disable @typescript-eslint/no-shadow */
import * as luxon from 'luxon';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Images } from '../../../../../../../../shared/app-constants';
import { useAxios } from '../../../../../../../../shared/hooks/useAxios';
import {
  ActivityGroupKey,
  ActivityType,
} from '../../../../../../../../shared/design-system/components/organisms/activity/types';
import store from '../../../../../../../../store/store';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils';
import { resetUnsubscribeRequestState } from '../../../../../../prospect-slice';
import ActivityTypeFilters from './components/activity-type-filters';
import ActivityLog from '../../../../../../../../shared/design-system/components/organisms/activity/activity-log';
import MasterLog from '../../../../../../../../shared/enums/master-log-enum';
import ProspectDetailActivitySkeleton from './prospect-detail-activity-skeleton';
import NoProspectDetailResultScreen from '../../../no-prospect-detail-result-screen/no-prospect-detail-result-screen';
import { ActivityBy } from '../../../../../../enums/prospect';
import { getUserTimeZone } from '../../../../../../../../shared/utils/user-details';

type ProspectActivityProps = {
  prospectId: number;
  t: (x: string) => ReactNode;
  fetchActivity: boolean;
  onActivityChange: (value: boolean) => void;
  activeTab: string;
};

const ProspectDetailActivity: React.FC<ProspectActivityProps> = ({
  prospectId,
  t,
  fetchActivity,
  onActivityChange,
  activeTab,
}) => {
  const state = store.getState();
  const dispatch = useDispatch();
  const timeZone = getUserTimeZone();
  const unsubscribeRequestStatus =
    state.prospect.unsubscribeProspectRequest.status;
  type ProspectActivityItem = {
    id: string;
    timestamp: string;
    log: string;
    key: string;
    activityBy?: string[];
  };

  const [items, setItems] = useState<ProspectActivityItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>([
    ActivityBy.Prospect,
    ActivityBy.User,
    ActivityBy.System,
  ]);
  const [prevProspectId, setPrevProspectId] = useState<number | null>(null);
  const [hasVisitedActivityTab, setHasVisitedActivityTab] = useState<
    Map<number, boolean>
  >(new Map());
  const [isInitialMount, setIsInitialMount] = useState(true);
  const hasReceivedResponseRef = useRef(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const getAllActivityTypes = () => [
    ActivityBy.Prospect,
    ActivityBy.User,
    ActivityBy.System,
  ];

  const getActivityByParam = (types: string[]): string[] | undefined => {
    const allTypes = getAllActivityTypes();
    const isAllSelected =
      types.length === allTypes.length &&
      allTypes.every((type) => types.includes(type));
    return isAllSelected ? undefined : types;
  };

  const getInitialParams = () => {
    const params: Record<string, any> = {
      pageNum: 1,
      contactId: prospectId,
    };
    const activityByParam = getActivityByParam(selectedActivityTypes);
    if (activityByParam) {
      params.activityBy = activityByParam;
    }
    return params;
  };

  const { loading, response, axiosConfigSetter } = useAxios(
    {
      url: '/activity/prospect',
      params: getInitialParams(),
    },
    [],
  );

  useEffect(() => {
    if (response === undefined || loading) {
      return;
    }
    const responseItems: ProspectActivityItem[] = Array.isArray(response)
      ? (response as any[]).map((raw) => ({
          // eslint-disable-next-line dot-notation, @typescript-eslint/dot-notation
          id: String(raw['_id']),
          timestamp: raw.timestamp,
          log: raw.log,
          key: raw.key,
          activityBy: raw.activityBy,
        }))
      : [];

    if (!hasReceivedResponseRef.current) {
      hasReceivedResponseRef.current = true;
    }

    // Continue fetching until the API returns an empty array
    setHasMore(responseItems.length > 0);

    setItems((prev) => {
      const prevItems = prev || [];

      const existingIds = new Set<string>();
      prevItems.forEach((item) => {
        existingIds.add(item.id);
      });

      const newItems = responseItems.filter(
        (item) => !existingIds.has(item.id),
      );

      if (newItems.length > 0) {
        // Merge items and sort by timestamp descending (newest first)
        // This ensures that newly created items (today) appear at the top
        const mergedItems = [...prevItems, ...newItems];
        const sortedItems = mergedItems.sort((a, b) => {
          const timestampA = luxon.DateTime.fromISO(a.timestamp).toMillis();
          const timestampB = luxon.DateTime.fromISO(b.timestamp).toMillis();
          return timestampB - timestampA; // Descending order (newest first)
        });
        return sortedItems;
      }
      return prevItems;
    });

    setIsFetching(false);
    setIsFilterLoading(false);

    // Mark initial mount as false after first response
    setIsInitialMount((prevIsInitialMount) => {
      if (prevIsInitialMount) {
        setHasVisitedActivityTab((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(prospectId, true);
          return newMap;
        });
        return false;
      }
      return prevIsInitialMount;
    });
  }, [response, loading, prospectId]);

  useEffect(() => {
    executeOnRequestStatus({
      status: unsubscribeRequestStatus,
      onSuccess: () => {
        const params: Record<string, any> = {
          pageNum: page,
          contactId: prospectId,
        };
        const activityByParam = getActivityByParam(selectedActivityTypes);
        if (activityByParam) {
          params.activityBy = activityByParam;
        }
        axiosConfigSetter({ params });
        dispatch(resetUnsubscribeRequestState());
      },
    });
  }, [unsubscribeRequestStatus]);

  // Reset state when prospectId changes (component remounts, but this ensures clean state)
  useEffect(() => {
    if (prevProspectId !== null && prevProspectId !== prospectId) {
      // Prospect changed - reset pagination and items
      setPage(1);
      setItems([]);
      setHasMore(true);
      setIsFetching(false);
      setIsInitialMount(true);
      hasReceivedResponseRef.current = false;
    }
    setPrevProspectId(prospectId);
  }, [prospectId, prevProspectId]);

  // Handle activity tab visibility and API calls
  useEffect(() => {
    if (activeTab !== 'activity') {
      return;
    }

    // Skip API call on initial mount - let useAxios handle it
    if (isInitialMount) {
      return;
    }

    const hasVisited = hasVisitedActivityTab.get(prospectId) || false;

    // Always fetch on first visit to activity tab for this prospect
    if (!hasVisited) {
      const newMap = new Map(hasVisitedActivityTab);
      newMap.set(prospectId, true);
      setHasVisitedActivityTab(newMap);
      setPage(1);
      setItems([]);
      setHasMore(true);
      setIsFetching(false);
      const params = getInitialParams();
      axiosConfigSetter({ params });
      return;
    }

    // If fetchActivity is true, it means an action was performed in another tab
    if (fetchActivity) {
      setPage(1);
      setHasMore(true);
      setIsFetching(false);
      const params = getInitialParams();
      axiosConfigSetter({ params });
      onActivityChange(false); // Reset the flag
    }
    // If fetchActivity is false and we've visited before, don't call API
  }, [
    activeTab,
    fetchActivity,
    prospectId,
    axiosConfigSetter,
    onActivityChange,
    isInitialMount,
    hasVisitedActivityTab,
  ]);

  const getProspectActivity = () => {
    if (isFetching) return;
    const nextPage = page + 1;
    setIsFetching(true);
    setPage(nextPage);
    const params: Record<string, any> = {
      pageNum: nextPage,
      contactId: prospectId,
    };
    const activityByParam = getActivityByParam(selectedActivityTypes);
    if (activityByParam) {
      params.activityBy = activityByParam;
    }
    axiosConfigSetter({ params });
  };

  const handleActivityTypeApply = (selectedTypes: string[]) => {
    setSelectedActivityTypes(selectedTypes);
    setPage(1);
    setItems([]);
    setHasMore(true);
    setIsFetching(false);
    setIsFilterLoading(true);
    const params: Record<string, any> = {
      pageNum: 1,
      contactId: prospectId,
    };
    const activityByParam = getActivityByParam(selectedTypes);
    if (activityByParam) {
      params.activityBy = activityByParam;
    }
    axiosConfigSetter({ params });
  };

  if ((!hasReceivedResponseRef.current && loading) || isFilterLoading) {
    return <ProspectDetailActivitySkeleton />;
  }

  // if the returned data is empty show no data icon
  // if (items.length === 0) {
  //   return (
  //     <div className="d-flex flex-column gap-20" style={{ height: '100%' }}>
  //       <NoProspectDetailResultScreen
  //         title="No Activities yet"
  //         description="Your interactions will show up here once you start engaging with this prospect."
  //         imageSrc={Images.NoProspectActivity}
  //       />
  //     </div>
  //   );
  // }

  const formatActivityGroupLabel = (
    timestamp: string,
  ): { label: string; groupKey: ActivityGroupKey } => {
    const activityDate = luxon.DateTime.fromISO(timestamp)
      .setZone(timeZone)
      .startOf('day');
    const today = luxon.DateTime.now().setZone(timeZone).startOf('day');
    const yesterday = today.minus({ days: 1 });
    const startOfThisWeek = today.startOf('week');
    const startOfLastWeek = startOfThisWeek.minus({ weeks: 1 });
    const endOfLastWeek = startOfThisWeek.minus({ days: 1 });

    if (activityDate.hasSame(today, 'day')) {
      return {
        label: t('labels.today') as string,
        groupKey: 'today',
      };
    }

    if (activityDate.hasSame(yesterday, 'day')) {
      return {
        label: t('labels.yesterday') as string,
        groupKey: 'yesterday',
      };
    }

    if (activityDate >= startOfThisWeek) {
      return {
        label: t('labels.this_week') as string,
        groupKey: 'thisWeek',
      };
    }

    if (activityDate >= startOfLastWeek && activityDate <= endOfLastWeek) {
      return {
        label: t('labels.last_week') as string,
        groupKey: 'lastWeek',
      };
    }

    return {
      label: activityDate.toFormat('LLLL yyyy'),
      groupKey: 'monthYear',
    };
  };

  const activityArrMap = new Map<string, ActivityType>();

  items.forEach(({ id, timestamp, log, key, activityBy }) => {
    const { label, groupKey } = formatActivityGroupLabel(timestamp);
    const mapKey = `${groupKey}-${label}`;

    if (!activityArrMap.has(mapKey)) {
      activityArrMap.set(mapKey, {
        dayText: label,
        groupKey,
        activity: [
          { log, key: key as MasterLog, timestamp, activityBy, _id: id },
        ],
      });
      return;
    }

    activityArrMap.get(mapKey)?.activity.push({
      log,
      key: key as MasterLog,
      timestamp,
      activityBy,
      _id: id,
    });
  });

  const toMillis = (ts: string) => luxon.DateTime.fromISO(ts).toMillis();

  const activityArr = Array.from(activityArrMap.values()).map((group) => ({
    ...group,
    activity: [...group.activity].sort(
      (a, b) =>
        toMillis(b.timestamp as string) - toMillis(a.timestamp as string),
    ),
  }));

  return (
    <>
      <div className="d-flex flex-column h-100 activity-detail-container">
        <ActivityTypeFilters
          selectedActivityTypes={selectedActivityTypes}
          onApply={handleActivityTypeApply}
        />
        {items.length === 0 ? (
          <NoProspectDetailResultScreen
            title="No Activities yet"
            description="Your interactions will show up here once you start engaging with this prospect."
            imageSrc={Images.NoProspectActivity}
          />
        ) : (
          <ActivityLog
            activityArr={activityArr}
            wrapperId="prospectActivity"
            scrollableTarget="prospectActivity"
            onLoadMore={getProspectActivity}
            hasMore={hasMore}
            lengthOfPage={items ? items.length : 0}
            wrapperStyle={{ overflow: 'auto' }}
          />
        )}
      </div>
    </>
  );
};

export default withTranslation()(ProspectDetailActivity);
